import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Renderer2,
  input,
  inject,
  viewChild,
  signal,
  afterRenderEffect,
} from '@angular/core';
import BezierEasing from 'bezier-easing';
import { KingService } from '../king.service';
import { KingHit } from '../interfaces/king.interface';
import { sleep } from '@core/utils/sleep.util';
import { KingUser } from '../interfaces/king.interface';
import { animateElement } from '@core/utils/deprecated.utils';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-king-hp-bar',
  templateUrl: './king-hp-bar.component.html',
  styleUrls: ['./king-hp-bar.component.scss'],
  standalone: true,
  imports: [DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KingHpBarComponent {
  readonly isChallenger = input.required<boolean>();
  readonly user = input.required<KingUser>();

  hpBar = viewChild.required('hpBar', { read: ElementRef });

  isAnimation = signal<boolean>(false);
  animatedHp = signal<number>(0);

  private readonly kingService = inject(KingService);
  private readonly render = inject(Renderer2);

  private hitAnimationEffect = afterRenderEffect(async () => {
    const { hits, GUN_ANIMATION_DURATION } = this.kingService;
    const lastHit = hits().at(-1);
    if (
      lastHit &&
      lastHit.by !== (this.isChallenger() ? 'challenger' : 'king')
    ) {
      const hpBefore =
        (lastHit.hp_before / 100) * (lastHit.toUser?.initial_hp ?? 0);

      await sleep(GUN_ANIMATION_DURATION - 2000);
      this.hitAnimation(lastHit, hpBefore);
    }
  });

  private async hitAnimation(data: KingHit, hpBefore: number) {
    if (data.damage === 0) {
      await sleep(300);
      this.displayMiss();
      return;
    }

    this.isAnimation.set(true);
    this.animatedHp.set(hpBefore);
    this.render.addClass(this.hpBar().nativeElement, 'hit');
    const damageSlider = this.render.createElement('div');
    this.render.addClass(damageSlider, 'damage-slider');
    damageSlider.style.width = `${
      (data.damage / this.user().initial_hp!) * 100
    }%`;
    this.render.appendChild(this.hpBar().nativeElement, damageSlider);
    await sleep(300);
    if (data.crit) {
      this.displayCriticalHit();
    }

    await animateElement(
      (p: number) => {
        let width = (data.damage / this.user().initial_hp!) * 100 * (1 - p);
        if (width < 0) {
          width = 0;
        }
        damageSlider.style.width = `${width}%`;
        this.animatedHp.set(Math.floor(hpBefore - data.damage * p));
      },
      500,
      BezierEasing(0, 0, 0, 0)
    );

    await sleep(1500);
    this.render.removeChild(this.hpBar().nativeElement, damageSlider);
    this.render.removeClass(this.hpBar().nativeElement, 'hit');
    this.isAnimation.set(false);
  }

  private async displayCriticalHit() {
    const critElement = this.render.createElement('div');
    this.render.addClass(critElement, 'critical');
    critElement.innerText = 'CRITICAL HIT';
    this.render.appendChild(this.hpBar().nativeElement, critElement);
    await sleep(1000);
    this.render.removeChild(this.hpBar().nativeElement, critElement);
  }

  private async displayMiss() {
    const critElement = this.render.createElement('div');
    this.render.addClass(critElement, 'miss');
    critElement.innerText = 'MISS';
    this.render.appendChild(this.hpBar().nativeElement, critElement);
    await sleep(1000);
    this.render.removeChild(this.hpBar().nativeElement, critElement);
  }
}
